<template>
  <div>
    <section class="relative md:py-24 py-16 overflow-hidden" id="review">

      <!-- Decorative background -->
      <div class="absolute inset-0 pointer-events-none overflow-hidden">
        <div class="absolute -top-32 -right-32 w-96 h-96 bg-orange-100 dark:bg-orange-900/10 rounded-full blur-3xl opacity-50"></div>
        <div class="absolute bottom-0 left-0 w-80 h-80 bg-amber-50 dark:bg-amber-900/5 rounded-full blur-3xl opacity-60"></div>
      </div>

      <div class="container relative z-10">
        <!-- Header -->
        <div class="grid grid-cols-1 pb-12 text-center review-header">
          <div class="inline-flex items-center gap-2 justify-center mb-3">
            <div class="h-px w-8 bg-orange-600"></div>
            <h6 class="text-orange-600 text-sm font-semibold uppercase tracking-widest">Reviews</h6>
            <div class="h-px w-8 bg-orange-600"></div>
          </div>
          <h3 class="mb-3 md:text-4xl text-3xl font-bold text-slate-900 dark:text-white leading-tight">
            Leave a <span class="text-orange-600">Review</span>
          </h3>
          <p class="text-slate-500 dark:text-slate-300 max-w-xl mx-auto leading-relaxed">
            Share your experience with us — your feedback helps us grow and serve you better!
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 md:grid-cols-2 mt-2 items-start gap-8">
          <!-- Form Column -->
          <div class="lg:col-span-8 form-reveal">
            <div class="p-8 rounded-3xl shadow-xl bg-white dark:bg-slate-900 border border-slate-100/80 dark:border-slate-700/50 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-100 dark:hover:shadow-orange-900/10">
              <form @submit.prevent="submitReview" class="space-y-6">
                <div class="grid lg:grid-cols-12 lg:gap-6 gap-4">
                  <!-- Full Name -->
                  <div class="lg:col-span-6 form-field" style="transition-delay: 0.05s">
                    <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      <i class="uil uil-user me-1 text-orange-500"></i> Full Name
                    </label>
                    <InputText
                      v-model="review.full_name"
                      name="review_full_name"
                      placeholder="Your Full Name"
                      class="w-full"
                      :class="{ 'p-invalid': errors.full_name?.[0] }"
                    />
                    <Transition name="error-bounce">
                      <small v-if="errors.full_name?.[0]" class="text-red-500 font-medium text-xs mt-1 block">
                        {{ errors.full_name[0] }}
                      </small>
                    </Transition>
                  </div>

                  <!-- Review Title -->
                  <div class="lg:col-span-6 form-field" style="transition-delay: 0.1s">
                    <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      <i class="uil uil-tag-alt me-1 text-orange-500"></i> Review Title
                    </label>
                    <InputText
                      v-model="review.title"
                      name="review_title"
                      placeholder="e.g. Amazing Quality!"
                      class="w-full"
                      :class="{ 'p-invalid': errors.title?.[0] }"
                    />
                    <Transition name="error-bounce">
                      <small v-if="errors.title?.[0]" class="text-red-500 font-medium text-xs mt-1 block">
                        {{ errors.title[0] }}
                      </small>
                    </Transition>
                  </div>
                </div>

                <!-- Description -->
                <div class="form-field" style="transition-delay: 0.15s">
                  <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    <i class="uil uil-comment-alt me-1 text-orange-500"></i> Your Review
                  </label>
                  <Textarea
                    v-model="review.description"
                    name="review_description"
                    placeholder="Write your review here..."
                    rows="5"
                    class="w-full"
                    :class="{ 'p-invalid': errors.description?.[0] }"
                    autoResize
                  />
                  <Transition name="error-bounce">
                    <small v-if="errors.description?.[0]" class="text-red-500 font-medium text-xs mt-1 block">
                      {{ errors.description[0] }}
                    </small>
                  </Transition>
                </div>

                <!-- Rating -->
                <div class="form-field" style="transition-delay: 0.2s">
                  <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    <i class="uil uil-star me-1 text-orange-500"></i> Rating
                  </label>

                  <!-- Star rating visual -->
                  <div class="flex gap-2 mb-3">
                    <button
                      v-for="star in 5"
                      :key="star"
                      type="button"
                      @click="review.rate = star.toString()"
                      class="star-btn text-3xl transition-all duration-200 hover:scale-125 focus:outline-none"
                      :class="star <= Number(review.rate) ? 'text-orange-500 scale-110' : 'text-slate-200 dark:text-slate-600 hover:text-orange-300'"
                    >
                      ★
                    </button>
                  </div>

                  <Dropdown
                    v-model="review.rate"
                    :options="ratingOptions"
                    name="review_rate"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Or select from dropdown"
                    class="w-full md:w-[50%]"
                    :class="{ 'p-invalid': errors.rate?.[0] }"
                  />
                  <Transition name="error-bounce">
                    <small v-if="errors.rate?.[0]" class="text-red-500 font-medium text-xs mt-1 block">
                      {{ errors.rate[0] }}
                    </small>
                  </Transition>
                </div>

                <!-- Images Upload -->
                <div class="form-field" style="transition-delay: 0.25s">
                  <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    <i class="uil uil-image me-1 text-orange-500"></i> Upload Images
                    <span class="text-slate-400 font-normal ms-1">(optional)</span>
                  </label>

                  <FileUpload
                    mode="basic"
                    multiple
                    name="review_images"
                    accept="image/*"
                    chooseLabel="Choose Images"
                    class="p-button-sm w-full sm:w-auto"
                    @select="onFileSelect"
                    auto
                    customUpload
                  />

                  <!-- Preview with bounce-in animation -->
                  <TransitionGroup name="img-pop" tag="div"
                    v-if="imagePreview.length"
                    class="mt-4 flex flex-wrap gap-3"
                  >
                    <div
                      v-for="(src, index) in imagePreview"
                      :key="index"
                      class="relative w-20 h-20 group"
                    >
                      <img
                        :src="src"
                        class="w-20 h-20 object-cover rounded-xl border-2 border-slate-200 dark:border-slate-600 shadow-sm group-hover:shadow-md transition-all duration-300 group-hover:scale-105"
                        alt="Review image preview"
                      />
                      <button
                        type="button"
                        @click="removeImage(index)"
                        class="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow transition-all duration-200 hover:scale-110"
                      >✕</button>
                    </div>
                  </TransitionGroup>
                </div>

                <!-- Submit Button -->
                <div class="form-field" style="transition-delay: 0.3s">
                  <Button
                    type="submit"
                    name="review_submit"
                    :label="loading ? 'Submitting...' : 'Submit Review'"
                    icon="pi pi-check"
                    :loading="loading"
                    class="submit-btn w-full sm:w-auto"
                    :disabled="loading"
                  />
                </div>
              </form>
            </div>
          </div>

          <!-- Side Info -->
          <div class="lg:col-span-4 side-reveal">
            <div class="lg:ms-4 flex flex-col gap-6">
              <!-- Star card -->
              <div class="side-card text-center p-8 rounded-3xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-xl shadow-orange-500/25">
                <div class="text-6xl mb-4 animate-star-pulse">⭐</div>
                <h5 class="text-xl font-bold mb-3">We Value Your Feedback</h5>
                <p class="text-orange-100 text-sm leading-relaxed">
                  Your honest opinion means a lot to us. Help others and guide us to make your experience even better.
                </p>
              </div>

              <!-- Info cards -->
              <div v-for="info in sideInfo" :key="info.title"
                   class="info-card p-5 rounded-2xl bg-white dark:bg-slate-900 shadow-md border border-slate-100 dark:border-slate-700/50 hover:shadow-lg hover:border-orange-200 dark:hover:border-orange-800/40 transition-all duration-300 flex items-center gap-4">
                <div class="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <i :class="`uil uil-${info.icon} text-orange-600 text-xl`"></i>
                </div>
                <div>
                  <p class="font-semibold text-slate-800 dark:text-white text-sm">{{ info.title }}</p>
                  <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{{ info.desc }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useToast } from "primevue/usetoast";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Dropdown from "primevue/dropdown";
import FileUpload from 'primevue/fileupload'
import Button from "primevue/button";
import store from "@/store";

const toast = useToast();
const loading = ref(false);

const ratingOptions = ref([
  { label: "⭐ 1 Star",  value: "1" },
  { label: "⭐⭐ 2 Stars", value: "2" },
  { label: "⭐⭐⭐ 3 Stars", value: "3" },
  { label: "⭐⭐⭐⭐ 4 Stars", value: "4" },
  { label: "⭐⭐⭐⭐⭐ 5 Stars", value: "5" },
]);

const imagePreview = ref([])
const review = ref({
  full_name: "",
  title: "",
  description: "",
  rate: "",
  images: []
});

const errors = ref({
  full_name: [],
  rate: [],
  title: [],
  description: [],
  images: []
});

const sideInfo = ref([
  { icon: 'shield-check', title: 'Safe & Private', desc: 'Your review data is kept secure.' },
  { icon: 'clock', title: 'Quick Review', desc: 'Published within 24 hours.' },
  { icon: 'users-alt', title: 'Help Others', desc: 'Your feedback guides future customers.' },
]);

const showSuccess = (detail) => {
  toast.add({ severity: "success", summary: "Thank you! 🎉", detail, life: 4000 });
};

const onFileSelect = (event) => {
  const filesArray = Array.from(event.files || []);
  filesArray.forEach(file => {
    imagePreview.value.push(URL.createObjectURL(file))
  })
  review.value.images.push(...filesArray);
}

const removeImage = (index) => {
  imagePreview.value.splice(index, 1);
  review.value.images.splice(index, 1);
}

function submitReview() {
  loading.value = true;
  store.dispatch("createReview", review.value)
    .then(() => {
      review.value = { full_name: "", title: "", description: "", rate: "", images: [] };
      imagePreview.value = [];
      showSuccess("Review submitted. Pending team review and will be published within 24 hours.");
    })
    .catch((err) => {
      errors.value = err.response?.data?.errors || {};
    })
    .finally(() => {
      loading.value = false;
    });
}

// Scroll reveal
let observer;
onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.review-header, .form-reveal, .side-reveal, .form-field').forEach(el => {
    observer.observe(el);
  });
});
onUnmounted(() => { if (observer) observer.disconnect(); });
</script>

<style scoped>
/* Reveal animations */
.review-header {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.review-header.revealed { opacity: 1; transform: translateY(0); }

.form-reveal {
  opacity: 0;
  transform: translateX(-40px);
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s;
}
.form-reveal.revealed { opacity: 1; transform: translateX(0); }

.side-reveal {
  opacity: 0;
  transform: translateX(40px);
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s;
}
.side-reveal.revealed { opacity: 1; transform: translateX(0); }

.form-field {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.form-field.revealed { opacity: 1; transform: translateY(0); }

/* Star pulse */
@keyframes starPulse {
  0%, 100% { transform: scale(1) rotate(-5deg); }
  50% { transform: scale(1.2) rotate(5deg); }
}
.animate-star-pulse { animation: starPulse 2.5s ease-in-out infinite; }

/* Star buttons */
.star-btn { line-height: 1; display: inline-block; }

/* Error bounce transition */
.error-bounce-enter-active { animation: bounceIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.error-bounce-leave-active { animation: bounceIn 0.2s ease reverse; }
@keyframes bounceIn {
  from { opacity: 0; transform: scale(0.7) translateY(-5px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

/* Image pop transition */
.img-pop-enter-active { animation: imgPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.img-pop-leave-active { animation: imgPop 0.25s ease reverse; }
@keyframes imgPop {
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 1; transform: scale(1); }
}

/* PrimeVue overrides */
:deep(.p-inputtext),
:deep(.p-dropdown),
:deep(.p-inputtextarea) {
  @apply dark:bg-slate-800 dark:text-white dark:border-slate-600 border-slate-200 rounded-xl transition-all duration-200;
}
:deep(.p-inputtext:focus),
:deep(.p-inputtextarea:focus) {
  @apply ring-2 ring-orange-500/40 border-orange-500;
}
:deep(.p-dropdown:focus-within) {
  @apply ring-2 ring-orange-500/40 border-orange-500 rounded-xl;
}
:deep(.p-invalid) {
  @apply border-red-400;
}
:deep(.p-button) {
  @apply bg-orange-600 hover:bg-orange-700 border-orange-600 hover:border-orange-700 rounded-xl text-white font-semibold shadow-md shadow-orange-600/20 transition-all duration-300;
}
:deep(.p-button:hover) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(234, 88, 12, 0.35);
}
:deep(.p-button:disabled) {
  @apply opacity-60 cursor-not-allowed;
}
:deep(.p-fileupload-basic) {
  @apply rounded-xl overflow-hidden;
}
:deep(.p-fileupload-basic .p-button) {
  @apply !bg-slate-700 !border-slate-700 hover:!bg-slate-600;
}
</style>
